import { formatNumbers, Text, Tile } from '@lune-fe/lune-ui-lib'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import InsertInvitationOutlinedIcon from '@mui/icons-material/InsertInvitationOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import { Box, Stack } from '@mui/material'
import Divider from '@mui/material/Divider'
import moment from 'moment'
import { FC, ReactNode, useMemo } from 'react'

import { Report } from 'endpoints/dapi'
import useAccounts from 'hooks/useAccounts'

export const SourceType: Record<string, string> = {
    lune: 'Lune',
    ecotransit: 'EcoTransIT CSV',
    lune_logistics_v2: 'Lune v2 CSV template',
    lune_fintech: 'Spend CSV',
    rfq: 'Tender',
    logistics_user_template: 'Lune template',
    unknown: '-',
}
const LocalTile = ({ title, icon }: { title: string; icon: ReactNode }) => {
    return (
        <Tile
            sx={{
                svg: {
                    color: `TextPrimary`,
                    width: '20px',
                    height: '20px',
                },
            }}
            icon={icon}
        >
            <Text variant={'body2'}>{title}</Text>
        </Tile>
    )
}

const ReportDetails: FC<{
    item: Report
}> = ({ item }) => {
    const title = `${item.type === 'logistics' ? 'Logistics' : 'Spend'} CSV Report`
    const { accounts } = useAccounts()

    // Create a map of account ID to account name for efficient lookup
    const accountNameMap = useMemo(() => {
        const map = new Map<string, string>()
        accounts.forEach((account) => {
            if (account.id && account.name) {
                map.set(account.id, account.name)
            }
        })
        return map
    }, [accounts])

    // Get account names for the filter IDs
    const accountNames = useMemo(() => {
        if (!item.filters.accountIds) {
            return []
        }
        return item.filters.accountIds
            .map((accId) => accountNameMap.get(accId))
            .filter((name): name is string => name !== undefined)
    }, [item.filters.accountIds, accountNameMap])

    return (
        <Stack direction={'column'} spacing={4}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'column',
                }}
            >
                <Text variant={'h4'}>{title}</Text>
                <Text variant={'body2'} color={`TextSecondary`}>
                    {moment(item.createdAt).format('MMMM D, YYYY')}
                </Text>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {item.numCalculations && (
                    <LocalTile
                        icon={<TableChartOutlinedIcon />}
                        title={`${formatNumbers(item.numCalculations, 0)} emissions
                                calculations`}
                    />
                )}
                {item.fileSize && (
                    <LocalTile icon={<DescriptionOutlinedIcon />} title={item.fileSize} />
                )}
            </Box>
            <Divider />
            <Text variant={'h6'}>Filters</Text>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {item.filters.from && item.filters.through && (
                    <LocalTile
                        icon={<InsertInvitationOutlinedIcon />}
                        title={`${moment(item.filters.from).format('D MMM, YYYY')} - ${moment(item.filters.through).format('D MMM, YYYY')}`}
                    />
                )}
                {item.filters.accountIds && accountNames.length > 0 && (
                    <LocalTile
                        icon={<AccountBalanceWalletOutlinedIcon />}
                        title={`Accounts: ${accountNames.join(', ')}`}
                    />
                )}
                {item.filters.source && (
                    <LocalTile
                        icon={<FolderOutlinedIcon />}
                        title={`Source: ${SourceType[item.filters.source]}`}
                    />
                )}
                {item.filters.shipmentStatus && (
                    <LocalTile
                        icon={<LocalShippingOutlinedIcon />}
                        title={`Shipment status: ${item.filters.shipmentStatus === 'shipment' ? 'Shipment' : 'Pre-shipment'}`}
                    />
                )}
            </Box>
        </Stack>
    )
}

export default ReportDetails
